import { ActionButton, CardFrame, Chip } from '../parts/index.js';
// The store question (R30, B199), as a triage card beside the tier questions (U18, B191).
//
// A route decides where a counterparty's mail goes before anything reads it. When an admin
// retires a wrong one, what it already moved into its owner's private store is not moved
// back and not left silently: each item becomes one of these questions, asked of the owner
// alone (R30). "The business's" moves it back to the company's store in the same transaction
// as the answer (migration 59), where it waits to be sorted before anyone else reads it;
// "mine" keeps it and closes the question. One item at a time and never a batch: a store
// question has no pattern, so no answer here settles anything but the item on the card.
//
// The card is the owner's and nobody else's by construction: the questions live in the
// owner's personal store, which no other login can open (R17), and a manager's view never
// reaches one (B55). So the card needs no rule of its own about who may see it; when the API
// returns no questions it draws nothing at all (R5).
//
// The look is QuestionsCard's (`.item.questions`) with the private marker in violet (D77,
// U20's --private tokens), because the item is in the private store until it is answered.
// Mounted by the main session in Triage.tsx beside QuestionsCard; nothing here reaches into
// that file.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, Unauthenticated } from '../api.js';
import { formatDate } from '../labels.js';
import { railsMe, type StoreAnswer, type StoreQuestion } from '../rails.js';

type Load = { kind: 'loading' } | { kind: 'ready'; questions: StoreQuestion[] } | { kind: 'error'; message: string };

const KINDS: Record<string, string> = {
  email: 'Email', teams_chat: 'Teams chat', teams_channel: 'Teams channel', meeting: 'Meeting', calendar_event: 'Calendar event',
  document: 'Document', call: 'Call', whatsapp: 'WhatsApp', note: 'Note',
};

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** What the answer did, in words, for the line under the next card. */
export function saidLine(answer: StoreAnswer, subject: string | null): string {
  const what = subject ? `“${subject}”` : 'That item';
  return answer === 'commercial'
    ? `${what} is back in the company's store. It is sorted there before anyone else can read it.`
    : `${what} stays in your private store.`;
}

export function StoreQuestionCard({ initiallyOpen = true }: { initiallyOpen?: boolean }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [open, setOpen] = useState(initiallyOpen);
  const [at, setAt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    railsMe.storeQuestions()
      .then((r) => { if (!cancelled) { setLoad({ kind: 'ready', questions: r.questions }); setAt(0); } })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setLoad({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      });
    return () => { cancelled = true; };
  }, []);

  const questions = load.kind === 'ready' ? load.questions : [];
  const q = questions[at];

  const answer = useCallback((belongs: StoreAnswer) => {
    if (!q) return;
    setBusy(true); setProblem(null);
    railsMe.answerStore(q.store, q.id, belongs)
      .then(() => { setSaid(saidLine(belongs, q.subject)); setAt((i) => i + 1); })
      .catch((e: unknown) => {
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        // 409 "answered meanwhile": another sitting settled it; move on rather than stop.
        if (e instanceof ApiError && e.status === 409 && /answered/.test(e.message)) { setAt((i) => i + 1); return; }
        setProblem(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setBusy(false));
  }, [q]);

  // Nothing to ask is nothing drawn: no placeholder (R5). A read that failed says so once.
  if (load.kind === 'loading') return null;
  if (load.kind === 'ready' && questions.length === 0) return null;
  if (load.kind === 'error') {
    return (
      <CardFrame private className="questions" data-item="store-questions">
        <p className="plain">Cobrain could not read your questions about the private store just now. {load.message}</p>
      </CardFrame>
    );
  }

  const left = Math.max(questions.length - at, 0);
  return (
    <CardFrame private className="questions" data-item="store-questions" aria-labelledby="store-q-title">
      <div className="meta">
        <Chip variant="private">Private · only you</Chip>
        {q ? <span>question {at + 1} of {questions.length} · a route that was retired put {left === 1 ? 'this' : `these ${left}`} in your private store</span> : null}
        <span className="when">
          {q ? <><ActionButton reading className="link inline" disabled={busy} onClick={() => setAt((i) => i + 1)}>skip</ActionButton> · </> : null}
          <ActionButton reading className="link inline" onClick={() => setOpen((v) => !v)}>{open ? 'not now' : 'answer'}</ActionButton>
        </span>
      </div>

      {!q ? (
        <>
          <p className="head" id="store-q-title">That is the last of them.</p>
          {said ? <p className="note said">{said}</p> : null}
        </>
      ) : !open ? (
        <p className="head" id="store-q-title">Where does this belong: {q.subject ?? 'an item with no subject'}?</p>
      ) : (
        <div aria-live="polite" key={q.id}>
          <p className="head" id="store-q-title">Where does this belong: {q.subject ?? <span className="dim">(no subject)</span>}?</p>
          <p className="gist">
            {q.kind ? (KINDS[q.kind] ?? q.kind) : 'An item'}
            {q.fromName || q.fromAddress ? <> from {q.fromName ?? q.fromAddress}</> : null}
            {q.occurredAt ? <> · {formatDate(q.occurredAt)}</> : null}
          </p>
          {q.preview ? <p className="private">{q.preview}</p> : null}
          <p className="note" style={{ marginTop: 'var(--pad-y)' }}>
            A rule sent this sender's mail to your private store, and an admin has since retired that rule. Nobody else has
            seen this item and nobody will be told what you choose. This settles this one item only.
          </p>
          <p className="q">What would you like to do?</p>
          <div className="opts">
            <ActionButton type="button" className="opt lead" disabled={busy} onClick={() => answer('commercial')}>
              It is the business's <small>move it back to the company's store</small>
            </ActionButton>
            <ActionButton type="button" className="opt" disabled={busy} onClick={() => answer('personal')}>
              It is mine <small>keep it private</small>
            </ActionButton>
          </div>
          {problem ? <p className="note said">{problem}</p> : null}
          {said ? <p className="note said">{said}</p> : null}
          <p className="settles">{plural(left, 'item')} left to decide.</p>
        </div>
      )}
    </CardFrame>
  );
}
